import React, { useContext } from 'react';
import { Stack, Chip, Select, MenuItem, Tooltip, Box } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { DraftPolicySection, DraftPolicySubSection, DraftPolicy, DraftManual } from '../../../_types/manuals/draft';
import { usePolicyEditorContext } from './context';
import { DraftManualDetailContext } from '../context';
import { AppContext } from 'views/App/context';
import useHasAccreditationSolution from 'views/App/Portal/AWB/_hooks/useHasAccreditationSolution';
import ruiAccreditationTagsData from 'views/App/Portal/Policy/DraftManuals/_data_sources/ruiAccreditationTags.json';
import draftManualHelper from '../../helpers/draftManualHelper';

interface SectionTagsProps {
  section: DraftPolicySection | DraftPolicySubSection;
  editable?: boolean;
  agencyModified?: boolean;
  handleEditLevelTagChange?: (editLevelTag: string) => void;
  policyData: DraftPolicy | undefined | null;
  manual: DraftManual;
}

const SectionTags: React.FC<SectionTagsProps> = React.memo(
  ({ handleEditLevelTagChange = () => {}, section, editable = false, agencyModified = false, policyData, manual }) => {
    const editLevelTag = section.editLevelTag ?? 'AGENCY_CONTENT';
    const { accreditationTags } = usePolicyEditorContext();
    const { showAccreditationTags } = useContext(DraftManualDetailContext);
    const { isFeatureEnabled } = useContext(AppContext);
    const hasAwbSubscription = useHasAccreditationSolution();

    // Custom lookup for awbRUIData feature
    const getRuiAccreditationTags = (): string[] => {
      if (!isFeatureEnabled('awbRUIData')) {
        return [];
      }

      if (!policyData) {
        return [];
      }

      // Calculate the full section number including policy number
      const policyNumber = draftManualHelper.getNumberManualNode(policyData, manual);
      let sectionNum: string;

      // Check if this is a subsection by looking for parentId
      if ('parentId' in section && section.parentId) {
        // This is a subsection
        const parentSection = policyData.sections.find((s) => s.id === section.parentId);

        if (parentSection) {
          const parentSectionIndex = draftManualHelper.getEnabledSectionIndex(parentSection.id, policyData);
          const subSectionIndex = draftManualHelper.getEnabledSubSectionIndex(parentSection.id, section.id, policyData);
          sectionNum = `${policyNumber}.${parentSectionIndex}.${subSectionIndex}`;
        } else {
          sectionNum = `${policyNumber}.${draftManualHelper.getNumberPolicyNode(section, policyData)}`;
        }
      } else {
        // This is a main section
        const sectionIndex = draftManualHelper.getEnabledSectionIndex(section.id, policyData);
        sectionNum = `${policyNumber}.${sectionIndex}`;
      }

      const sectionTitle = section.title;

      // Look for exact match by section number and title
      const ruiEntry = ruiAccreditationTagsData[sectionNum as keyof typeof ruiAccreditationTagsData];

      if (ruiEntry && ruiEntry.title.toLowerCase() === sectionTitle.toLowerCase()) {
        return ruiEntry.tags || [];
      }

      return [];
    };

    const ruiTags = getRuiAccreditationTags();
    const hasRuiTags = ruiTags.length > 0;

    return (
      <Stack
        direction='row'
        spacing={3}
      >
        {editable ? (
          <Select
            id={`edit_level_tag_${section.id}`}
            label={false}
            value={editLevelTag}
            size='small'
            onChange={({ target }) => {
              handleEditLevelTagChange(target.value);
              setTimeout(() => {
                document.body.dispatchEvent(new Event('auto-save-policy'));
              }, 1000);
            }}
            sx={{
              borderRadius: '4px',
              fontSize: '0.75rem',
              fontWeight: 600,
              height: '1.25rem',
              lineHeight: '1.25rem',
              textTransform: 'uppercase',
            }}
          >
            {['FEDERAL', 'STATE', 'BEST_PRACTICE', 'DISCRETIONARY'].map((editLevel: string) => (
              <MenuItem
                key={`editLevelTag_${editLevel}`}
                value={editLevel}
              >
                {t(`draftManuals.editLevelTag.${editLevel}`)}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Chip
            label={t(`draftManuals.editLevelTag.${editLevelTag}`)}
            variant='outlined'
            color='neutral'
            sx={{ textTransform: 'uppercase' }}
          />
        )}
        {agencyModified && (
          <Chip
            label={t('draftManuals.agencyModified')}
            variant='outlined'
            color='neutral'
            sx={{ textTransform: 'uppercase' }}
          />
        )}
        {/* Render RUI accreditation tags when feature is enabled */}
        {hasRuiTags && hasAwbSubscription && (
          <>
            <Chip
              label={ruiTags[0]}
              variant='filled'
              color='info'
            />
            {ruiTags.length > 1 && (
              <Tooltip
                title={
                  <Box>
                    {ruiTags.slice(1).map((tag, index) => (
                      <Box key={index}>{tag}</Box>
                    ))}
                  </Box>
                }
                placement='top'
              >
                <Chip
                  label={`+${ruiTags.length - 1}`}
                  variant='filled'
                  color='info'
                />
              </Tooltip>
            )}
          </>
        )}
        {/* Render existing accreditation tags when RUI tags are not available */}
        {showAccreditationTags && !hasRuiTags && hasAwbSubscription && accreditationTags[section.id]?.length > 0 && (
          <>
            <Chip
              label={accreditationTags[section.id][0]}
              variant='filled'
              color='info'
            />
            {accreditationTags[section.id].length > 1 && (
              <Tooltip
                title={
                  <Box>
                    {accreditationTags[section.id].slice(1).map((tag, index) => (
                      <Box key={index}>{tag}</Box>
                    ))}
                  </Box>
                }
                placement='top'
              >
                <Chip
                  label={`+${accreditationTags[section.id].length - 1}`}
                  variant='filled'
                  color='info'
                />
              </Tooltip>
            )}
          </>
        )}
      </Stack>
    );
  }
);

export default SectionTags;
